import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  SearchField,
  Skeleton,
} from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';
import { useLibraryFiles } from '@/hooks/useLibraryFiles';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { toChatAttachment } from '@/hooks/useAskAboutFile';
import type { ChatAttachment, LibraryFile } from '@/services/DataService';
import { LIBRARY_PREFETCH_MARGIN, LIBRARY_SEARCH_SETTLE_MS } from '@/constants/library';
import { LibraryThumbnail } from './LibraryList';
import { fileEntry } from './libraryEntry';

interface LibraryPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Files the composer already holds: shown, but not picked twice. */
  attachedIds: string[];
  /** How many more files the composer has room for. */
  maxSelectable: number;
  /** Hands the picked files over, in the order they were picked. */
  onAdd: (files: ChatAttachment[]) => void;
}

/** When a file was uploaded, as the picker's second line shows it: "Oct 13, 3:53 PM". */
const UPLOADED_AT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * The composer's "From library": the workspace's uploads, newest first and
 * searchable by name, picked several at a time and attached without being
 * uploaded again.
 *
 * Opening it mounts the picker afresh, so a search or a selection never
 * carries over from the last time; nothing is fetched until it opens.
 */
export function LibraryPickerDialog({
  open, onOpenChange, attachedIds, maxSelectable, onAdd,
}: LibraryPickerDialogProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        ariaLabel="Add from library"
        // `ModalContent` caps itself at 26rem; a list of file names needs more.
        className="flex h-[min(36rem,85vh)] w-full max-w-[min(40rem,calc(100vw-2rem))] flex-col overflow-hidden p-0"
      >
        <LibraryPicker
          attachedIds={attachedIds}
          maxSelectable={maxSelectable}
          onCancel={() => onOpenChange(false)}
          onAdd={(files) => {
            onAdd(files);
            onOpenChange(false);
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function LibraryPicker({ attachedIds, maxSelectable, onCancel, onAdd }: {
  attachedIds: string[];
  maxSelectable: number;
  onCancel: () => void;
  onAdd: (files: ChatAttachment[]) => void;
}) {
  const [ search, setSearch ] = useState('');
  const [ asked, setAsked ] = useState('');
  const [ picked, setPicked ] = useState<LibraryFile[]>([]);
  const [ scroller, setScroller ] = useState<HTMLDivElement | null>(null);

  // Answered by the server, so it waits for typing to settle, as the Library does.
  useEffect(() => {
    const settle = setTimeout(() => setAsked(search.trim()), LIBRARY_SEARCH_SETTLE_MS);
    return () => clearTimeout(settle);
  }, [ search ]);

  const {
    data,
    isPending,
    isError,
    isPlaceholderData,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useLibraryFiles({ scope: 'all', type: null, search: asked });

  const files = useMemo(() => {
    const seen = new Set<string>();
    return (data?.pages ?? []).flatMap((page) => page.files).filter((file) => {
      if (seen.has(file.id)) return false;
      seen.add(file.id);
      return true;
    });
  }, [ data ]);

  const setSentinel = useInfiniteScroll({
    root: scroller,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    paused: isFetchNextPageError || isPlaceholderData,
    rootMargin: LIBRARY_PREFETCH_MARGIN,
  });

  const attached = useMemo(() => new Set(attachedIds), [ attachedIds ]);
  const pickedIds = useMemo(() => new Set(picked.map((file) => file.id)), [ picked ]);
  const full = picked.length >= maxSelectable;

  const toggle = (file: LibraryFile) => {
    setPicked((held) => (held.some((candidate) => candidate.id === file.id)
      ? held.filter((candidate) => candidate.id !== file.id)
      : [ ...held, file ]));
  };

  let body: ReactNode;
  if (isError && !data) {
    body = (
      <PickerNote title="Could not load your library" description="Something went wrong reading your files.">
        <Button variant="secondary" size="sm" onClick={() => refetch()}>Try again</Button>
      </PickerNote>
    );
  } else if (isPending) {
    body = (
      <div role="status" aria-busy="true" aria-label="Loading files" className="flex flex-col gap-1 px-3 py-2">
        {Array.from({ length: 6 }, (_unused, row) => (
          <div key={row} className="flex h-14 items-center gap-3 px-3">
            <Skeleton className="size-9 shrink-0 rounded-v2-md" />
            <Skeleton className="h-3.5 flex-1" style={{ maxWidth: `${40 + ((row * 17) % 35)}%` }} />
          </div>
        ))}
      </div>
    );
  } else if (files.length === 0) {
    body = asked ? (
      <PickerNote title={`No files match “${asked}”`} description="Try a different name, or clear the search." />
    ) : (
      <PickerNote
        title="Your library is empty"
        description="Files you upload, or attach in a chat, show up here."
      />
    );
  } else {
    body = (
      <div
        role="listbox"
        aria-label="Files"
        aria-multiselectable="true"
        aria-busy={isPlaceholderData || undefined}
        className={cn(
          'flex flex-col gap-0.5 px-3 py-2 transition-opacity duration-v2-base',
          isPlaceholderData && 'pointer-events-none opacity-50',
        )}
      >
        {files.map((file) => {
          const already = attached.has(file.id);
          const selected = pickedIds.has(file.id);
          const unavailable = already || (!selected && full);
          return (
            <div
              key={file.id}
              role="option"
              aria-selected={selected || already}
              aria-disabled={unavailable || undefined}
              tabIndex={unavailable ? -1 : 0}
              onClick={() => {
                if (!unavailable) toggle(file);
              }}
              onKeyDown={(event) => {
                if ((event.key === 'Enter' || event.key === ' ') && !unavailable) {
                  event.preventDefault();
                  toggle(file);
                }
              }}
              className={cn(
                'flex h-14 items-center gap-3 rounded-v2-md px-3 outline-none transition-colors duration-v2-quick',
                'focus-visible:shadow-[inset_0_0_0_2px_hsl(var(--v2-ring))]',
                selected ? 'bg-v2-list-selected' : !unavailable && 'cursor-pointer hover:bg-v2-list-hover',
                unavailable && !already && 'opacity-50',
              )}
            >
              <LibraryThumbnail entry={fileEntry(file)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-medium leading-[1.4] text-v2-list-ink">
                  {file.filename}
                </span>
                <span className="block truncate text-[12px] leading-[1.4] text-v2-list-faint">
                  {already ? 'Already attached' : UPLOADED_AT.format(new Date(file.created_at))}
                </span>
              </span>
              <span
                aria-hidden
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors duration-v2-quick',
                  selected || already
                    ? 'border-v2-foreground bg-v2-foreground text-v2-background'
                    : 'border-v2-border bg-v2-card text-transparent',
                  already && 'opacity-50',
                )}
              >
                <Check className="size-3" strokeWidth={3} />
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <ModalHeader bordered title="Add from library" />
      <div className="border-b border-v2-border px-6 py-3">
        <SearchField
          value={search}
          onChange={setSearch}
          label="Search library"
          placeholder="Search by name..."
          autoFocus
        />
      </div>

      <div ref={setScroller} className="min-h-0 flex-1 overflow-y-auto">
        {body}
        <div ref={setSentinel} aria-hidden className="h-px" />
        {isFetchingNextPage ? (
          <div role="status" className="flex items-center justify-center gap-2 pb-4 pt-1 text-[12.5px] text-v2-list-faint">
            <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden />
            Loading more files
          </div>
        ) : null}
        {isFetchNextPageError && !isFetchingNextPage ? (
          <div role="alert" className="flex items-center justify-center gap-3 pb-4 pt-1 text-[12.5px] text-v2-list-faint">
            Couldn&apos;t load more files.
            <Button variant="secondary" size="xs" onClick={() => fetchNextPage()}>Retry</Button>
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-v2-border bg-v2-muted px-6 py-3.5">
        <p aria-live="polite" className="min-w-0 truncate text-v2-xs text-v2-muted-foreground">
          {picked.length > 0 ? `${picked.length} selected` : `Pick up to ${maxSelectable} ${maxSelectable === 1 ? 'file' : 'files'}`}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button disabled={picked.length === 0} onClick={() => onAdd(picked.map(toChatAttachment))}>
            Add to chat
          </Button>
        </div>
      </div>
    </>
  );
}

/** What the list says when it has no files to show, with an optional way on. */
function PickerNote({ title, description, children }: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-16 text-center">
      <p className="text-[14px] font-medium text-v2-list-ink">{title}</p>
      <p className="text-[12.5px] text-v2-list-faint">{description}</p>
      {children ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}
